import { inject, Injectable, Injector } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { Action, Store } from '@ngrx/store';
import { debounceTime, map, switchMap, take } from 'rxjs/operators';
import { ShoppingCartService } from '../services/shopping-cart.service';
import { State } from '../reducers';
import { CheckoutActions, ShopCartActions, SparePartActions } from '../actions';
import * as SparePartSelectors from '../reducers/spare-part.reducer';
import * as ShopCartSelectors from '../reducers/shop-cart.reducer';
import { ProductsService } from '../services';
import {
  IAddToCartItem,
  ICart, ICartAttributes,
  ICartBaseInformation,
  ICartItemListError,
  ICartItemListsRequest,
  ICartPayload,
  ICartRule,
  ICartUpdateRequest,
  ICartUpdateRequestAttributes,
  ICartSwitchDecision,
  IVatIdResponse,
  ITotalWeight,
} from '../models/cart.models';
import { ICPQStartConfigRequest } from '../models/cpq.models';
import { CatalogFacade } from './catalog.facade';
import { ISapMessage } from '../models/sap.model';
import {
  IAddress,
  IMaterialMasterNumbersPerItem, IMaterialMasterNumbersToggles,
  IPriceDisputingPerItem,
  ISystemDetails,
} from '../models/common.models';
import { EFeatureToggles, EGlueResource } from '../configurations/common';
import { IShsEquipmentDataAttributes, IShsEquipments } from '../models/installedbase.models';
import { IGuestCart, IGuestCartResponse } from '../models/guest.models';
import {
  selectAddressFromGuestCart, selectCartAttributesFromGuestCart,
  selectCustomerCheckoutDataFromGuestCart,
  selectGuestCart,
} from '../reducers/guest-cart.reducer';
import { ICustomerCheckoutData } from '../models/customer.models';
import { ConfigurationFacade } from './configuration.facade';

@Injectable({
  providedIn: 'root',
})
export class MarketingFacade {
  private readonly injector: Injector = inject(Injector);
  private readonly store: Store<State> = inject(Store<State>);
  private readonly catalogFacade: CatalogFacade = inject(CatalogFacade);
  private readonly configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);

  readonly currentCartId$: Observable<string | null> = this.store.select(ShopCartSelectors.selectCurrentCartId);

  private pShoppingCartService: ShoppingCartService | undefined;
  private pProductService: ProductsService | undefined;

  public getShoppingCartService(): ShoppingCartService {
    if (!this.pShoppingCartService) {
      this.pShoppingCartService = this.injector.get(ShoppingCartService);
    }
    return this.pShoppingCartService;
  }

  public get productService(): ProductsService {
    if (!this.pProductService) {
      this.pProductService = this.injector.get(ProductsService);
    }
    return this.pProductService;
  }

  postCart(attributes: ICartUpdateRequestAttributes = {}): Observable<ICartPayload> {
    return this.getShoppingCartService().postCart(attributes);
  }

  /**
   * Create new cart for the user.
   *
   * Can also set attributes to the cart using the optional {@param attributes}.
   *
   * As the cart id is not known until cart creation, the recommended method for checking
   * if the cart has been created and is now the current default is to subscribe to the
   * current cart id via {@see selectCartId()} and check until the value has changed relative
   * to the previous cart ID value (or null if no current cart ID was set previously).
   *
   * @param {ICartUpdateRequestAttributes} attributes
   */
  createEmptyCart(attributes?: ICartUpdateRequestAttributes): void {
    this.resetCachedCheckoutData();
    this.store.dispatch(ShopCartActions.createEmptyCart({attributes}));
  }

  addItemToNewCart(requestBody: any, product: any, redirectUrl: string): void {
    this.store.dispatch(ShopCartActions.postNewCartWithItem({requestBody, product, redirectUrl}));
  }

  addItemsFromCsvToCart(cartId: string, items: IAddToCartItem[]): void {
    const requestBody: ICartItemListsRequest = {
      data: {
        type: EGlueResource.ITEM_LISTS,
        attributes: {
          items,
        },
      },
    };
    this.store.dispatch(ShopCartActions.addToCartFromCsvFile({cartId, requestBody}));
  }

  isAddingItemsViaCsvInProgress(): Observable<boolean> {
    return this.store.select(ShopCartSelectors.selectAddToCartViaCsvProgress);
  }

  selectAddToCartViaCsvErrors(): Observable<Array<ICartItemListError>> {
    return this.store.select(ShopCartSelectors.selectAddToCartViaCsvErrors);
  }

  reorderItem(item: any): void {
    this.store.dispatch(SparePartActions.addItem({item}));
  }

  reorderAllItems(items: any[]): void {
    this.store.dispatch(SparePartActions.addAllItems({items}));
  }

  updateReorderWithSystemDetails(updatedSystemDetails: ISystemDetails): void {
    this.store.dispatch(SparePartActions.updateReorderWithSystemDetails({updatedSystemDetails}));
  }

  updateReorderWithSystemDetailsForFunctionalLocation(updatedSystemDetails: IShsEquipmentDataAttributes): void {
    this.store.dispatch(SparePartActions.updateReorderWithSystemDetailsForFunctionalLocation({updatedSystemDetails}));
  }

  getReorderItems(): Observable<any> {
    return this.store.select(SparePartSelectors.getReorderItems);
  }

  isReorderPending(): Observable<boolean> {
    return this.store.select(SparePartSelectors.isReorderPending);
  }

  isReorderConfirmed(): Observable<boolean> {
    return this.store.select(SparePartSelectors.isReorderConfirmed);
  }

  confirmReorder(): void {
    this.store.dispatch(SparePartActions.confirmReorder());
  }

  completeReorder(): void {
    this.store.dispatch(SparePartActions.completeReorder());
  }

  cancelReorder(): void {
    this.store.dispatch(SparePartActions.cancelReorder());
  }

  getCarts(): Observable<any> {
    return this.getShoppingCartService().getCarts();
  }

  getCartById(cartId: string): Observable<ICartPayload> {
    return this.getShoppingCartService().getCartById(cartId);
  }

  getGuestCartById(guestUserId: string, cartId: string): Observable<IGuestCartResponse> {
    return this.getShoppingCartService().getGuestCartById(guestUserId, cartId);
  }

  selectGuestCart(): Observable<IGuestCart | null> {
    return this.store.select(selectGuestCart);
  }

  selectAddressFromGuestCart(): Observable<IAddress | null> {
    return this.store.select(selectAddressFromGuestCart);
  }

  selectCustomerCheckoutDataFromGuestCart(): Observable<ICustomerCheckoutData | null> {
    return this.store.select(selectCustomerCheckoutDataFromGuestCart);
  }

  selectCartAttributesFromGuestCart(): Observable<ICartAttributes | null> {
    return this.store.select(selectCartAttributesFromGuestCart);
  }

  addItemToCart(cartId: string, itemData: any): Observable<ICartPayload> {
    return this.getShoppingCartService().addItemToCart(cartId, itemData);
  }

  deleteItemsFromCartAndUpdateSystemDetails(cartId: string, updateAttributes: ICartUpdateRequestAttributes): void {
    this.store.dispatch(ShopCartActions.deleteItemsFromCart({cartId: cartId, updateAttributes: updateAttributes}));
  }

  deleteItemsFromCart(cartId: string): Observable<ICartPayload> {
    return this.getShoppingCartService().deleteItemsFromCart(cartId);
  }

  addItemsToCartFromCsvFile(cartId: string, requestBody: ICartItemListsRequest): Observable<ICartPayload> {
    return this.getShoppingCartService().postProductsFromCsvToCart(cartId, requestBody);
  }

  addItemToExistingCart(cartId: string, requestBody: any, product: any, redirectUrl: string): void {
    this.store.dispatch(ShopCartActions.checkIsCartDefault({cartId, requestBody, product, redirectUrl}));
  }

  getCartItems(cartId: string): Observable<ICartPayload> {
    return this.getShoppingCartService().getCartItems(cartId);
  }

  getCartShipments(cartId: string): Observable<ICartPayload> {
    return this.getShoppingCartService().getCartShippingMethods(cartId);
  }

  loadCurrentCart(): void {
    this.store.dispatch(ShopCartActions.loadCurrentCartAndCartItems());
  }

  selectLastAddedItemName(): Observable<string | null> {
    return this.store.select(ShopCartSelectors.lastAddedItemName);
  }

  shopCartPageDataSelector(): Observable<any> {
    return this.store.select(ShopCartSelectors.getShopCartPageData);
  }

  getCurrentCartItems(): Observable<any> {
    return this.store.select(ShopCartSelectors.selectCartItems);
  }


  deleteItemFromCart(cartId: string, itemId: string): Observable<{}> {
    return this.getShoppingCartService().deleteItemFromCart(cartId, itemId);
  }

  sequentialRequestPatchCartItemGetCartItems(cartId: string, itemId: string, itemData: any): Observable<ICartPayload> {
    return this.getShoppingCartService().sequentialRequestPatchCartItemGetCartItems(cartId, itemId, itemData);
  }

  selectCart(): Observable<ICart> {
    return this.store.select(ShopCartSelectors.selectCurrentCart);
  }

  selectCartById(cartId: string): Observable<ICart | undefined> {
    return this.store.select(ShopCartSelectors.getCarts).pipe(
      take(1),
      map((carts: ICart[]) => carts.find((cart: ICart) => cart.id === cartId)),
    );
  }

  /**
   * Triggers actions to switch the current/default cart to the one identified by {@param newDefaultCartId}.
   *
   * Optionally also allows updating some additional fields of the cart being switched to using the
   * optional {@param attributes}.
   *
   * To check if the new cart has been set as default, subscribe to the current cart or cart ID value
   * using {@see selectCartId}, {@see getCurrentCartItems} or {@see selectCart}.
   *
   * @param {string} newDefaultCartId
   * @param {ICartUpdateRequestAttributes|undefined} attributes
   */
  switchDefaultCart(newDefaultCartId: string, attributes?: ICartUpdateRequestAttributes) {
    this.resetCachedCheckoutData();
    this.store.dispatch(ShopCartActions.switchDefaultCart({cartId: newDefaultCartId, attributes}));
  }

  resetCachedCheckoutData(): void {
    this.store.dispatch(CheckoutActions.resetDeliveryDetailsFormData());
    this.store.dispatch(CheckoutActions.resetDeliveryDetailsApproverFormData());
    this.store.dispatch(CheckoutActions.resetTaxNumberFormData());
    this.store.dispatch(CheckoutActions.resetCartVouchers());
  }

  selectCarts(): Observable<Array<ICart>> {
    return this.store.select(ShopCartSelectors.getCarts);
  }

  selectCartsBaseInfo(): Observable<ICartBaseInformation[]> {
    return this.store.select(ShopCartSelectors.selectCartsBaseInfo);
  }

  selectCartsItems(): Observable<any> {
    return this.store.select(ShopCartSelectors.getCartsItems);
  }

  selectAllCartsItems(): Observable<any> {
    return this.store.select(ShopCartSelectors.getAllCartsItems);
  }

  selectMiniCartState(): Observable<any> {
    return this.store.select(ShopCartSelectors.selectMiniCartState);
  }

  /**
   * Resolves the pending cart switch decision by making the newly created cart (with the added item) the current cart.
   */
  switchToNewCart(): void {
    this.resolveCartSwitchDecision(pending => ShopCartActions.switchToNewCart({pending}));
  }

  /**
   * Resolves the pending cart switch decision by keeping the current cart as default.
   * The newly created cart (with the added item) remains in the user's cart list.
   */
  stayOnCurrentCart(): void {
    this.resolveCartSwitchDecision(pending => ShopCartActions.stayOnCurrentCart({pending}));
  }

  toggleMiniCart(isOpen: boolean): void {
    return this.store.dispatch(ShopCartActions.toggleMiniCart({isOpen}));
  }

  isAddItemOperationInProgress(): Observable<boolean> {
    return this.store.select(ShopCartSelectors.isAddItemToCartOperationInProgress);
  }

  selectItemsInQueueToCart(): Observable<string[]> {
    return this.store.select(ShopCartSelectors.itemsInQueueToCart);
  }

  postVoucher(cartId: string, code: any): Observable<ICartPayload> {
    return this.getShoppingCartService().postVoucher(cartId, code);
  }

  validateVatIdTaxNumber(vatId: string): Observable<IVatIdResponse> {
    return this.getShoppingCartService().validateVatIdTaxNumber(vatId);
  }

  removeVoucher(cartId: string, voucherId: string): Observable<ICartPayload> {
    return this.getShoppingCartService().removeVoucher(cartId, voucherId);
  }

  getProductBySku(sku: string): Observable<any> {
    return this.productService.getProductBySku(sku);
  }

  selectCurrency(): Observable<string | undefined> {
    return this.store.select(ShopCartSelectors.selectCurrentCartCurrency);
  }

  selectIsCartEmpty(): Observable<boolean> {
    return this.store.select(ShopCartSelectors.isCartEmpty);
  }

  selectCartId(): Observable<string | null> {
    return this.store.select(ShopCartSelectors.selectCurrentCartId);
  }

  selectCartItemsWithDetails(): Observable<any[]> {
    return this.store.select(ShopCartSelectors.cartItemsWithDetails);
  }

  selectCartItemsTotalWeight$(): Observable<ITotalWeight> {
    return this.store.select(ShopCartSelectors.selectCartItemsTotalWeight);
  }

  selectHasCartContract(): Observable<boolean> {
    return this.selectCartItemsWithDetails().pipe(map((items: any[]) => {
      return items ? items.some((item: any) => !!item.attributes.cpqConfigId) : false;
    }));
  }

  addProductToCart(product: any, isConcrete: boolean = false, quantity: number = 1, redirectUrl: string = ''): void {
    this.addItemToQueue(product.sku);
    this.currentCartId$.pipe(take(1), debounceTime(0),
      switchMap(cartId => this.hasDifferentSapProductSparePartTypeInCart(product).pipe(
        map(createNewCart => ({cartId, createNewCart})),
      )),
    ).subscribe(({cartId, createNewCart}) => {
      const addToCart = (requestBody: any): void => {
        if (createNewCart) {
          this.addItemToNewCartWithCopiedAttributes(requestBody, product);
        } else {
          this.addToCartDependingOnWhetherItExists(cartId, requestBody, product, redirectUrl);
        }
      };

      if (isConcrete) {
        addToCart(this.getAddToCartRequestBody(product, product.sku, quantity));
      } else {
        this.getProductBySku(product.sku).pipe(take(1)).subscribe({
          next: products => addToCart(this.getAddToCartRequestBody(
            product,
            products.data.attributes.attributeMap.product_concrete_ids[0],
            quantity,
          )),
          error: error => {
            this.removeItemFromQueue(product.sku, error);
          },
        });
      }
    });
  }

  /**
   * Creates a new cart with the item and asks the user which cart should stay active.
   * The system details, sold-to ID and sold-to/shipping addresses are copied from the current cart.
   * @param requestBody - add to cart request body
   * @param product - product being added
   */
  private addItemToNewCartWithCopiedAttributes(requestBody: any, product: any): void {
    this.selectCart().pipe(take(1)).subscribe(cart => {
      const attributes: ICartAttributes = cart?.attributes;

      this.store.dispatch(ShopCartActions.postNewCartWithItemForSwitchDecision({
        currentCartId: cart?.id,
        requestBody,
        product,
        cartAttributes: {
          systemDetails: attributes?.systemDetails,
          customerSoldToId: attributes?.customerSoldToId?.toString(),
          soldToAddress: attributes?.soldToAddress,
          shippingAddress: attributes?.shippingAddress,
        },
      }));
    });
  }

  /**
   * Checks whether the current cart contains an item with a different `sap_product_sparepart` than the product.
   * Returns false when the product has no `sap_product_sparepart` or the mixed cart restriction toggle is disabled.
   * @param product - product being added
   * @returns {Observable<boolean>} true if the item must be added to a new cart
   */
  private hasDifferentSapProductSparePartTypeInCart(product: any): Observable<boolean> {
    if (!product.sap_product_sparepart) {
      return of(false);
    }

    return this.configurationFacade.isFeatureEnabled(EFeatureToggles.MIXED_SAP_PRODUCT_IDENTIFIER_CART_RESTRICTION).pipe(
      take(1),
      switchMap(isRestrictionEnabled => {
        if (!isRestrictionEnabled) {
          return of(false);
        }

        return this.selectCartItemsWithDetails().pipe(
          take(1),
          map(cartItems => !!cartItems?.some(item => {
            const itemSparePart = item.attributes?.attributes?.sap_product_sparepart;
            return itemSparePart && itemSparePart !== product.sap_product_sparepart;
          })),
        );
      }),
    );
  }

  /**
   * Dispatches the action created from the pending cart switch decision.
   * Does nothing when the decision was already made (the modal emits close after every button).
   * @param createAction - creates the action to dispatch from the pending decision
   */
  private resolveCartSwitchDecision(createAction: (pending: ICartSwitchDecision) => Action): void {
    this.store.select(ShopCartSelectors.selectPendingCartSwitchDecision).pipe(take(1)).subscribe(pending => {
      if (pending) {
        this.store.dispatch(createAction(pending));
      }
    });
  }

  getAddToCartRequestBody(product: any, sku: string, quantity: number): any {
    const body: any = {
      data: {
        type: 'items',
        attributes: {
          ...product,
          quantity,
          sku,
        },
      },
    };
    if (product.type === 'contract') {
      body.data.attributes.systemDetailsPerItem[0].itemId = sku;
    }
    return body;
  }

  addToCartDependingOnWhetherItExists(cartId: string | null, requestBody: any, product: any, redirectUrl: string): void {
    if (cartId === null) {
      this.addItemToNewCart(requestBody, product, redirectUrl);
    } else {
      this.addItemToExistingCart(cartId, requestBody, product, redirectUrl);
    }
  }

  startContractConfiguration(sku: string, siemensEquipmentId: string, materialNumber: string): void {
    this.addItemToQueue(sku);
    const url: string = `/service-configuration?selectedService=${sku}&fl-number=${siemensEquipmentId}&rel-product-sysivk=${materialNumber}`;
    this.catalogFacade.getShsEquipmentInstalledBaseSystemByFunctionalLocation(materialNumber, siemensEquipmentId)
      .pipe(take(1)).subscribe(
      (selectedSystems: IShsEquipments) => {
        const selectedSystem: IShsEquipmentDataAttributes | undefined = selectedSystems.data.find(
          (installBase: {attributes: IShsEquipmentDataAttributes}) =>
            installBase.attributes.materialNumber === materialNumber
            && installBase.attributes.siemensEquipmentId === siemensEquipmentId)?.attributes;

        if (selectedSystem?.siemensEquipmentId === siemensEquipmentId &&
          selectedSystem?.materialNumber === materialNumber) {
          const systemDetails: ISystemDetails = {
            serialNumber: selectedSystem.serialNumber ?? '',
            company: selectedSystem.company ?? '',
            materialNumber,
            siemensEquipmentId,
            companyBusinessUnit: selectedSystem.companyBusinessUnit ?? '',
            companyBusinessUnitNumber: selectedSystem.companyBusinessUnitNumber ?? '',
            nameEnUs: selectedSystem.materialName ?? '',
            namePtBr: selectedSystem.materialName ?? '',
            isSparePartsAvailable: selectedSystem.isSparePartsAvailable ?? false,
          };
          const product: ICPQStartConfigRequest = {
            sku,
            quantity: 1,
            name: '',
            type: 'contract',
            productConfigurationInstance: {
              isComplete: true,
              displayData: `{"Functional Location Number": "${siemensEquipmentId}"}`,
              configuration: `{"functional_location_number": "${siemensEquipmentId}"}`,
              configuratorKey: 'CPQ_CONFIGURATOR',
              availableQuantity: '1',
            },
            systemDetails,
            systemDetailsPerItem: [{
              itemId: sku,
              systemDetails,
            }],
          } as ICPQStartConfigRequest;
          this.addProductToCart(product, false, 1, url);
        }
      },
    );
  }

  addItemToQueue(sku: string): void {
    this.store.dispatch(ShopCartActions.addItemToQueue({sku}));
  }

  removeItemFromQueue(sku: string, error: any): void {
    this.store.dispatch(ShopCartActions.removeItemFromQueue({sku, error}));
  }

  selectRfqActive(): Observable<boolean> {
    return this.store.select(ShopCartSelectors.selectRfqActive);
  }

  selectCartRules(): Observable<Array<ICartRule>> {
    return this.store.select(ShopCartSelectors.selectCartRules);
  }

  updateCartData(cartId: string, cartData: ICartUpdateRequest, eTag: string): Observable<ICartPayload> {
    return this.getShoppingCartService().updateCart(cartId, cartData, eTag);
  }

  updateCartById(cartId: string, attributes: ICartUpdateRequestAttributes = {}): void {
    this.store.dispatch(ShopCartActions.startCartOperation());
    this.store.select(ShopCartSelectors.selectDefaultCartNameAndETag)
      .pipe(take(1))
      .subscribe((cartData: {name: string; eTag: string | null}) => {
        if (cartData) {
          if (Object.keys(attributes).length === 0) {
            // in case of empty attributes object, just set the current cart name
            // use case: changing the default cart to the one identified by cartId
            attributes = {
              name: cartData.name,
            };
          }

          const payload: ICartUpdateRequest = {
            data: {
              type: EGlueResource.CARTS,
              attributes,
            },
          };
          this.store.dispatch(ShopCartActions.updateCartData({cartId, cartData: payload, eTag: cartData.eTag ?? ''}));
        }
      });
  }

  deleteCartById(cartId: string): Observable<any> {
    this.resetCachedCheckoutData();
    return this.getShoppingCartService().deleteCartById(cartId);
  }

  deleteCart(cartId: string, redirectUrl: string = ''): void {
    this.store.dispatch(ShopCartActions.deleteCart({cartId, redirectUrl}));
  }

  deleteCpqCart(cartId: string, redirectUrl: string = ''): void {
    this.store.dispatch(ShopCartActions.deleteCpqCart({cartId, redirectUrl}));
  }

  refreshCart(cartId: string): void {
    this.store.dispatch(ShopCartActions.loadCartItems({cartId}));
  }

  selectCartOperationInProgress(): Observable<boolean> {
    return this.store.select(ShopCartSelectors.selectCartOperationInProgress);
  }

  selectLoadingCartDataInProgress(): Observable<boolean> {
    return this.store.select(ShopCartSelectors.selectLoadingCartDataInProgress);
  }

  quoteSummaryPageDeleteQuote(cartId: string, redirectUrl: string): void {
    this.store.dispatch(ShopCartActions.quoteSummaryPageDeleteQuote({cartId, redirectUrl}));
  }

  selectSapMessages(): Observable<Array<ISapMessage>> {
    return this.store.select(ShopCartSelectors.selectSapMessages);
  }

  selectPrefilledShipToAddressId(): Observable<string | undefined> {
    return this.store.select(ShopCartSelectors.selectPrefilledShipToAddressId);
  }

  selectPriceDisputing(): Observable<IPriceDisputingPerItem[]> {
    return this.store.select(ShopCartSelectors.selectPriceDisputingPerItem);
  }

  selectIsPriceDisputingSetInCart(): Observable<boolean | undefined> {
    return this.store.select(ShopCartSelectors.selectIsPriceDisputingSetInCart);
  }

  selectPrefilledBillToAddressId(): Observable<string | undefined> {
    return this.store.select(ShopCartSelectors.selectPrefilledBillToAddressId);
  }

  setMaterialMasterNumbers(materialMasterNumbers: IMaterialMasterNumbersPerItem, cartId: string): void {
    this.store.dispatch(ShopCartActions.addOrUpdateMaterialMasterNumbersPerItemAction({
      materialMasterNumbers: materialMasterNumbers,
      cartId: cartId,
    }));
  }

  selectMaterialMasterNumbersByItemId(cartId: string, itemId: string): Observable<IMaterialMasterNumbersPerItem | undefined> {
    return this.store.select(ShopCartSelectors.selectMaterialMasterNumbersByItemId(cartId, itemId));
  }

  selectAllMaterialMasterNumbersInCart(cartId: string): Observable<IMaterialMasterNumbersPerItem[] | undefined> {
    return this.store.select(ShopCartSelectors.selectAllMaterialMasterNumbersInCart(cartId));
  }

  /**
   * @Description Get toggles for material master numbers inputs showing
   * @returns {Observable<IMaterialMasterNumbersToggles>}
   */
  getMaterialMasterNumbersToggles(): Observable<IMaterialMasterNumbersToggles> {
    return combineLatest([
      this.configurationFacade.isFeatureEnabled(EFeatureToggles.SAP_P40_PEAK_NUMBER),
      this.configurationFacade.isFeatureEnabled(EFeatureToggles.SAP_P40_NOTIFICATION_NUMBER),
      this.configurationFacade.isFeatureEnabled(EFeatureToggles.SAP_P40_SOFTWARE_VERSION),
      this.configurationFacade.isFeatureEnabled(EFeatureToggles.SAP_P40_SERIAL_NUMBER),
    ]).pipe(
      map(([peakNumber, notificationNumber, softwareVersion, serialNumber]) => {
        return {
          peakNumber: peakNumber,
          notificationNumber: notificationNumber,
          softwareVersion: softwareVersion,
          serialNumber: serialNumber,
        };
      }),
    );
  }

  /**
   * selects whether all data from cart checkout is loaded.
   * @returns {Observable<boolean>}
   */
  selectAreAllDataLoadedForCheckout(): Observable<boolean> {
    return this.store.select(ShopCartSelectors.areAllDataLoadedForCheckout).pipe(
      map((areAllDataLoaded: boolean) => {
        return areAllDataLoaded;
      }),
    );
  }
}
